import classNames from "classnames";
import useFetch from "use-http";
import { map, set, toNumber } from "lodash";
import { updateIn } from "immutable";
import { useEffect, useState } from "react";
import { parse, LogGroup as ParsedLogGroup, LogLine as ParsedLogLine } from "~/lib/job-log";
import { formatLogTimestamp } from "~/lib/date";
import Emojify from "~/components/shared/Emojify";
import Icon from "~/components/shared/Icon";
import LegacyLoading from "~/components/shared/LegacyLoading";
import {
  Log,
  LogBody,
  LogContent,
  LogControl,
  LogGroup,
  LogGroupHeader,
  LogGroupName,
  LogGroupTime,
  LogHeader,
  LogLine,
  LogLineContent,
  LogLineNumber,
  LogLoading,
  LogMessage,
  LogTimestamp,
  RunningDurationProvider,
  runningDurationProps,
} from "~/components/shared/Log";
import { LogLineActionsDropdown } from "~/components/shared/LogLineActionsDropdown";
import { useUserSession } from "~/lib/useUserSession";
import { useHash } from "~/lib/useHash";
import { useLogLineNavigation } from "~/components/build/Show/lib/useLogLineNavigation";

// The parser is the source of truth for the shape of groups and lines.
type LogLine = ParsedLogLine;
type LogGroup = ParsedLogGroup;

type Log = {
  linesHaveTimestamps: boolean;
  totalLines: number;
  groups: Array<LogGroup>;
};

export default function LogViewer({
  logUrl,
  job,
}: {
  logUrl: string;
  job: {
    id: string;
    state: string;
    base_path: string;
    log_deleted_message: string;
    started_at_from_agent: number | null;
    finished_at_from_agent: number | null;
    permissions: {
      delete_job_log: {
        allowed: boolean;
      };
    };
  };
}) {
  const isDeleted = Boolean(job.log_deleted_message);
  const canDeleteLog =
    ["finished", "canceled", "timed_out"].includes(job.state) &&
    job?.permissions?.delete_job_log?.allowed;

  const [log, setLog] = useState<Log | null>(null);
  const [hash, setHash] = useHash();
  const [theme, setTheme] = useUserSession("code-theme", "default");
  const [timestamps, setTimestamps] = useUserSession("job-log-timestamps-on", false);
  const [utcTimestamps, setUtcTimestamps] = useUserSession("timestamps-utc", false);

  // Handle asynchronous group/line navigation
  const [group, line] = hash.slice(1).split("-").map(toNumber);
  useLogLineNavigation({ group, line });

  const { get, response, loading, error } = useFetch(logUrl);

  async function loadLog() {
    const data = await get();

    if (!response.ok) {
      return;
    }

    if (data) {
      const parsedLog = parse(
        {
          id: job.id,
          url: logUrl,
          truncated: false,
          startedAtFromAgent: job.started_at_from_agent,
          finishedAtFromAgent: job.finished_at_from_agent,
          // This page loads the log once and never polls, so we intentionally
          // leave `streaming` falsy: a running group keeps its spinner but does
          // not get a live-ticking bar/duration, which would tick to misleading
          // values if the job finished while the page stayed open. The live bar
          // is a build-page feature, where the log refreshes every 5s.
          streaming: false,
        },
        data,
      );

      const [groupNumber] = hash.slice(1).split("-").map(toNumber);

      if (groupNumber) {
        return setLog({
          ...parsedLog,
          groups: parsedLog.groups.map((group) => {
            if (group.number === groupNumber) {
              return {
                ...group,
                expanded: true,
              };
            }

            return group;
          }),
        });
      }

      setLog(parsedLog);
    } else {
      setLog({
        groups: [],
        linesHaveTimestamps: false,
        totalLines: 0,
      });
    }
  }

  useEffect(() => {
    if (!isDeleted) {
      loadLog();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleTheme() {
    setTheme(theme === "default" ? "solarized" : "default");
  }

  function scrollToEnd() {
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  }

  function toggleTimestamps() {
    setTimestamps(!timestamps);
  }

  function toggleUtcTimestamps() {
    setUtcTimestamps(!utcTimestamps);
  }

  function setAllExpanded(expanded: boolean) {
    if (!log) {
      return;
    }

    setLog({
      ...log,
      groups: map(log.groups, (group) => set(group, "expanded", expanded)),
    });
  }

  function toggleGroup(index: number) {
    if (!log) {
      return;
    }

    setLog(updateIn(log, ["groups", index, "expanded"], (expanded) => !expanded));
  }

  function lineHash(group: LogGroup, line?: LogLine) {
    return line ? `#${group.number}-${line.number}` : `#${group.number}`;
  }

  function isHighlighted(group: LogGroup, line?: LogLine) {
    return hash === lineHash(group, line);
  }

  function toggleHighlighted(group: LogGroup, line?: LogLine) {
    const nextHash = lineHash(group, line);

    if (hash === nextHash) {
      setHash("_");
    } else {
      setHash(nextHash);
    }
  }

  function permalinkUrl(hash: string) {
    const url = new URL(window.location.href);
    url.hash = hash;
    return url.toString();
  }

  function groupEndLine(group: LogGroup) {
    return group.lines[group.lines.length - 1]?.number ?? group.number;
  }

  function textWithOptionalTimestamp(text: string, startedAt?: LogLine["startedAt"] | null) {
    if (timestamps && log?.linesHaveTimestamps && startedAt) {
      return `${formatLogTimestamp(startedAt, utcTimestamps)}\t${text}`;
    }

    return text;
  }

  function getTextForLines(group: LogGroup, startLine: number, endLine: number) {
    const lines: string[] = [];

    if (group.number >= startLine && group.number <= endLine) {
      lines.push(textWithOptionalTimestamp(group.textContent, group.startedAt));
    }

    for (const line of group.lines) {
      if (line.number >= startLine && line.number <= endLine) {
        lines.push(textWithOptionalTimestamp(line.textContent, line.startedAt));
      }
    }

    return lines.join("\n");
  }

  return (
    <Log data-theme={theme} className={classNames({ "min-h-0 p-0": error || isDeleted })}>
      {!error && !isDeleted && (
        <LogHeader>
          <LogControl onClick={() => setAllExpanded(true)}>
            <Icon icon="heroicons/16/solid/plus" className="h-4 w-4" />
            Expand groups
          </LogControl>

          <LogControl onClick={() => setAllExpanded(false)}>
            <Icon icon="heroicons/16/solid/minus" className="h-4 w-4" />
            Collapse groups
          </LogControl>

          {log?.linesHaveTimestamps && (
            <LogControl onClick={toggleTimestamps}>
              <Icon icon="heroicons/16/solid/calendar" className="h-4 w-4" />
              {timestamps ? "Hide timestamps" : "Show timestamps"}
            </LogControl>
          )}

          <LogControl className="md:ml-auto" onClick={toggleTheme}>
            <Icon
              icon={theme === "default" ? "heroicons/16/solid/sun" : "heroicons/16/solid/moon"}
              className="h-4 w-4"
            />
            Theme
          </LogControl>

          {canDeleteLog && (
            <LogControl
              href={`${job.base_path}/delete_log`}
              data-method="delete"
              data-confirm="Permanently delete this job’s log?"
            >
              <Icon icon="heroicons/16/solid/trash" className="h-4 w-4" />
              Delete
            </LogControl>
          )}

          <LogControl href={`${job.base_path}/download.txt`}>
            <Icon icon="heroicons/16/solid/arrow-down-tray" className="h-4 w-4" />
            Download
          </LogControl>

          <LogControl onClick={scrollToEnd}>
            <Icon icon="heroicons/16/solid/chevron-double-down" className="h-4 w-4" />
            Jump to end
          </LogControl>
        </LogHeader>
      )}

      <LogBody>
        {loading && (
          <LogLoading>
            <LegacyLoading />
          </LogLoading>
        )}

        {isDeleted && (
          <LogMessage>
            <Emojify text={job.log_deleted_message} />
          </LogMessage>
        )}

        {error && <LogMessage>There was an error loading the log</LogMessage>}

        {log && (
          <RunningDurationProvider {...runningDurationProps(log.groups)}>
            <LogContent totalLines={log.totalLines}>
              {log.groups.map((group, index) => (
                <LogGroup key={group.id} data-testid={`log-group-${group.number}`}>
                  <LogGroupHeader
                    number={group.number}
                    highlighted={isHighlighted(group)}
                    deemphasized={group.deemphasized}
                  >
                    <LogLineNumber
                      number={group.number}
                      jobId={job.id}
                      actions={
                        <LogLineActionsDropdown
                          jobId={job.id}
                          lineNumber={group.number}
                          groupEndLine={groupEndLine(group)}
                          getTextForLines={(startLine, endLine) =>
                            getTextForLines(group, startLine, endLine)
                          }
                          permalinkUrl={permalinkUrl(lineHash(group))}
                        />
                      }
                      onClick={() => toggleHighlighted(group)}
                    />

                    {timestamps && (
                      <LogTimestamp
                        utc={utcTimestamps}
                        time={group.startedAt}
                        onClick={toggleUtcTimestamps}
                      />
                    )}

                    <LogGroupName
                      name={group.name}
                      expanded={group.expanded}
                      onClick={() => toggleGroup(index)}
                    />

                    <LogGroupTime
                      startedAt={group.startedAt}
                      finishedAt={group.finishedAt}
                      finished={group.finished}
                      durationPercentage={group.durationPercentage}
                      maxDuration={group.maxDuration}
                    />
                  </LogGroupHeader>

                  {group.lines.map((line) => (
                    <LogLine
                      hidden={!group.expanded}
                      key={line.number}
                      number={line.number}
                      highlighted={isHighlighted(group, line)}
                    >
                      <LogLineNumber
                        number={line.number}
                        jobId={job.id}
                        actions={
                          <LogLineActionsDropdown
                            jobId={job.id}
                            lineNumber={line.number}
                            getTextForLines={(startLine, endLine) =>
                              getTextForLines(group, startLine, endLine)
                            }
                            permalinkUrl={permalinkUrl(lineHash(group, line))}
                          />
                        }
                        onClick={() => toggleHighlighted(group, line)}
                      />

                      {timestamps && (
                        <LogTimestamp
                          utc={utcTimestamps}
                          time={line.startedAt}
                          onClick={toggleUtcTimestamps}
                        />
                      )}

                      <LogLineContent colSpan={2}>
                        <span dangerouslySetInnerHTML={{ __html: line.line }} />
                      </LogLineContent>
                    </LogLine>
                  ))}
                </LogGroup>
              ))}
            </LogContent>
          </RunningDurationProvider>
        )}
      </LogBody>
    </Log>
  );
}
